import { useEffect, useState } from "react";
import { History, ChevronDown, ArrowRight, Bot } from "lucide-react";
import { Skeleton } from "./ui/skeleton";
import api from "../lib/api";
import { formatDate, formatDateTime } from "../lib/utils-format";

function displayValue(v) {
  if (v === undefined || v === null || v === "") return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) return formatDate(v);
  return String(v);
}

function activityLabel(a) {
  if (a.action === "created") return "Created";
  if (a.action === "archived") return "Archived";
  if (a.action === "deleted") return "Deleted";
  if (a.action === "engagement") return a.detail || "Engagement";
  if (a.field) return `Updated ${a.field}`;
  return a.action ? a.action.charAt(0).toUpperCase() + a.action.slice(1) : "Updated";
}

// Reusable Activity Timeline used on the Lead record and Work Queue record pages.
export function ActivityLog({ recordId, refreshToken, defaultOpen = false, className = "mt-8" }) {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(defaultOpen);

  useEffect(() => {
    if (!recordId) return;
    setLoading(true);
    api
      .get(`/activities?record_id=${recordId}&limit=200&sort_by=created_at&sort_dir=desc`)
      .then((r) => setActivities(r.data.items || []))
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  }, [recordId, refreshToken]);

  return (
    <div className={`${className} rounded-lg border border-border bg-card overflow-hidden`} data-testid="activity-log-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-6 py-4 border-b border-border bg-secondary/30 hover:bg-secondary/50 transition-colors"
        data-testid="activity-log-toggle"
      >
        <div className="flex items-center gap-2 text-sm font-semibold">
          Activity Timeline
          <span className="text-xs font-normal text-muted-foreground">({activities.length})</span>
        </div>
        <ChevronDown className={`h-4 w-4 text-muted-foreground transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div data-testid="activity-log-body">
          {loading ? (
            <div className="px-6 py-6"><Skeleton className="h-24 w-full" /></div>
          ) : activities.length === 0 ? (
            <div className="px-6 py-8 text-center text-sm text-muted-foreground">No activity recorded yet.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-muted-foreground border-b border-border">
                    <th className="px-6 py-3 font-medium">Activity</th>
                    <th className="px-6 py-3 font-medium">Who Made The Change</th>
                    <th className="px-6 py-3 font-medium whitespace-nowrap">Date &amp; Time</th>
                    <th className="px-6 py-3 font-medium">Change</th>
                  </tr>
                </thead>
                <tbody>
                  {activities.map((a) => (
                    <tr key={a.id} className="border-b border-border/50 last:border-0 align-top" data-testid={`activity-row-${a.id}`}>
                      <td className="px-6 py-3 font-medium whitespace-nowrap">{activityLabel(a)}</td>
                      <td className="px-6 py-3 whitespace-nowrap" data-testid={`activity-actor-${a.id}`}>
                        {a.actor_type === "ai_agent" ? (
                          <span className="inline-flex items-center gap-1.5 text-foreground">
                            <Bot className="h-3.5 w-3.5 text-primary" />
                            {a.actor_name || "AI Agent"}
                            <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-primary/15 text-primary">AI</span>
                          </span>
                        ) : (
                          <span className="text-muted-foreground">{a.actor_name || "System"}</span>
                        )}
                      </td>
                      <td className="px-6 py-3 text-muted-foreground whitespace-nowrap">{formatDateTime(a.created_at)}</td>
                      <td className="px-6 py-3">
                        {a.action === "updated" && (a.old_value !== undefined || a.new_value !== undefined) ? (
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="rounded bg-secondary/60 px-2 py-0.5 text-xs text-muted-foreground line-through">{displayValue(a.old_value)}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
                            <span className="rounded bg-primary/15 px-2 py-0.5 text-xs font-medium text-foreground">{displayValue(a.new_value)}</span>
                          </div>
                        ) : a.action === "engagement" && a.new_value ? (
                          <span className="text-xs text-foreground">{displayValue(a.new_value)}</span>
                        ) : (
                          <span className="text-xs text-muted-foreground">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
